<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import {
  registerModal as aclRegisterModal,
  unregisterModal as aclUnregisterModal,
} from 'dashboard/composables/aclModalCoordinator';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  showCloseButton: {
    type: Boolean,
    default: false,
  },
  backdropClass: {
    type: String,
    default: 'bg-black/45 backdrop-blur-[4px]',
  },
  backdropBlur: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['6xl', '5xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const isOpen = ref(false);

const maxWidthClass = computed(() => {
  const classesMap = {
    '6xl': 'max-w-6xl',
    '5xl': 'max-w-5xl',
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

const normalizedTitle = computed(() =>
  props.title.trim().toLocaleLowerCase('pt-BR')
);

function close() {
  aclUnregisterModal(close);
  emit('close');
  dialogRef.value?.close();
  isOpen.value = false;
}

const isAceleresCreateTaskDialog = computed(
  () => normalizedTitle.value === 'criar tarefa para mim'
);

function open() {
  aclRegisterModal(close);
  isOpen.value = true;
  dialogRef.value?.showModal();
}

onBeforeUnmount(() => aclUnregisterModal(close));

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => e.target === dialogRef.value && close();

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] === dialogRef.value) close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full transition-all duration-300 ease-in-out shadow-xl rounded-xl"
      :class="[
        maxWidthClass,
        positionClass,
        overflowYAuto ? 'overflow-y-auto' : 'overflow-visible',
        { 'dialog-without-backdrop-blur': !backdropBlur },
      ]"
      @close.prevent="handleDialogClose"
    >
      <div
        class="fixed inset-0 z-0"
        :class="backdropClass"
        aria-hidden="true"
        @click="handleClickOutside"
      />
      <form
        class="relative z-10 flex flex-col w-full h-auto gap-6 p-6 overflow-visible text-start align-middle transition-all duration-300 ease-in-out transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-xl"
        :class="{
          'aceleres-create-task-dialog': isAceleresCreateTaskDialog,
        }"
        @submit.prevent="confirm"
        @click.stop
      >
        <Button
          v-if="showCloseButton"
          icon="i-lucide-x"
          variant="ghost"
          color="slate"
          size="xs"
          type="button"
          :aria-label="t('DIALOG.BUTTONS.CANCEL')"
          class="absolute top-4 ltr:right-4 rtl:left-4"
          @click="close"
        />
        <div
          v-if="title || description"
          class="flex flex-col gap-2"
          :class="showCloseButton ? 'ltr:pr-8 rtl:pl-8' : ''"
        >
          <h3 class="text-base font-medium leading-6 text-n-slate-12">
            {{ title }}
          </h3>
          <slot name="description">
            <p v-if="description" class="mb-0 text-sm text-n-slate-11">
              {{ description }}
            </p>
          </slot>
        </div>
        <slot v-if="isOpen" />
        <!-- Dialog content will be injected here -->
        <slot name="footer">
          <div
            v-if="showCancelButton || showConfirmButton"
            class="flex items-center justify-between w-full gap-3"
            :class="{
              'aceleres-create-task-footer': isAceleresCreateTaskDialog,
            }"
          >
            <Button
              v-if="showCancelButton"
              variant="faded"
              color="slate"
              :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
              class="w-full"
              :class="{
                'aceleres-create-task-button aceleres-create-task-cancel':
                  isAceleresCreateTaskDialog,
              }"
              type="button"
              @click="close"
            />
            <Button
              v-if="showConfirmButton"
              :color="type === 'edit' ? 'blue' : 'ruby'"
              :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
              class="w-full"
              :class="{
                'aceleres-create-task-button': isAceleresCreateTaskDialog,
              }"
              :is-loading="isLoading"
              :disabled="disableConfirmButton || isLoading"
              type="submit"
            />
          </div>
        </slot>
      </form>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

dialog.dialog-without-backdrop-blur::backdrop {
  @apply backdrop-blur-none;
}

.dialog-position-top {
  margin-top: clamp(2rem, 5vh, 5rem);
  margin-bottom: auto;
}

.aceleres-create-task-button {
  border-radius: 50px !important;
}

.aceleres-create-task-dialog :deep(> :last-child),
.aceleres-create-task-footer {
  position: static !important;
  inset: auto !important;
  bottom: auto !important;
  flex-shrink: 0;
}

.aceleres-create-task-cancel {
  border: 1px solid #d1d5db !important;
}

:global(.dark) .aceleres-create-task-cancel {
  border-color: #4b5563 !important;
}
</style>
